import type { PayHubCashierContractsCashierSettingsResponse } from 'store/models';

/**
 * Card verification status, owned by this feature.
 *
 * Deliberately NOT `KycVerificationStatus` from `store/models`: that belongs to the separate
 * `kyc_card_verification` feature and would disappear with it. Either feature may be removed
 * independently, so neither may depend on the other's types.
 *
 * Numeric values follow the backend contract, so payloads map without translation.
 */
export enum ReservationCardStatus {
  inProgress = 1,
  verified = 2,
  notVerified = 3,
  failed = 4,
}

/**
 * Per the contract, a `KycVerificationResult` of 3 or 4 both render red: once the backend has
 * answered, "not verified" is a rejection, not a neutral "nothing uploaded yet".
 *
 * The neutral reading of 3 still exists — a stored card that has simply never been confirmed — so
 * this is deliberately *not* folded into the enum. The call site decides which reading applies
 * based on whether a result has arrived, the same way `CardVerificationController` does.
 */
export const isCardProofRejected = (status: ReservationCardStatus | null): boolean =>
  status === ReservationCardStatus.notVerified || status === ReservationCardStatus.failed;

/**
 * Both flags come from `GET /v1/payments/groups/init` → `cashier_settings`, per the backend
 * contract. They are declared here rather than in `store/models/APIModels.ts` because that file is
 * regenerated from swagger and is shared code; this keeps the feature removable in one directory.
 *
 * Delivery mirrors the existing `kyc_card_verification` flag: booleans on `cashier_settings`,
 * arriving inside `widget_settings` with the init response.
 */
export interface ReservationSettings extends PayHubCashierContractsCashierSettingsResponse {
  /** Master switch. `false` → the player gets the ordinary cashier, this feature stays dormant. */
  two_stage_deposit_enabled?: boolean;
  /**
   * Whether the player's account has passed KYC.
   *
   * The deposit side has no other source for this: nothing in `APIModels.ts` exposes an account KYC
   * status, and the existing account-verification state (`accountVerifBlocked`, `verificationData`)
   * lives only in the withdrawal slice.
   *
   * The form needs it *before* submit — the reservation snapshot only arrives after the hold.
   */
  user_verified?: boolean;
}

/**
 * Minimal shape of the shared Redux state this feature reads.
 *
 * Declared locally on purpose: neither `DepositSlice` nor `WithdrawalSlice` is exported from its
 * slice, and depending on the full types would couple the feature to shared code it must not own.
 *
 * Both flows carry their own copy of the settings, mirroring how `kyc_card_verification` is read
 * from `deposit.ts:221` and `withdrawal.ts:241`.
 */
export interface ReservationRootState {
  deposit?: {
    widgetSettings?: ReservationSettings;
  };
  withdrawal?: {
    widgetSettings?: ReservationSettings;
  };
}

/**
 * Where the player currently is in the reservation lifecycle.
 *
 * Intentionally coarse — one stage per *screen* in the design, not per visual state. The design
 * shows a single "Кошти зарезервовано" screen whose upload cell changes appearance (idle →
 * countdown → error → verified); those are states of `FileUpload`, not separate stages.
 *
 * There is also no "collect amount" stage: that happens in the normal deposit flow before this
 * feature is ever entered.
 */
export enum ReservationStage {
  /** Funds held, but the player's account is not KYC-verified → exit to external KYC. */
  needsAccountKyc = 'needs-account-kyc',
  /** Funds held, account verified, card proof outstanding (incl. in-progress and failed). */
  needsCardProof = 'needs-card-proof',
  /** Card verified — funds will be credited. */
  done = 'done',
  /**
   * The hold ran out before verification finished and the money went back to the card.
   *
   * Distinct from `absent` on purpose: "nothing was ever held" needs no explanation, but "we took
   * your money and gave it back" does. Without this the player silently lands on the methods list
   * with no idea why the previous deposit never arrived.
   */
  expired = 'expired',
}

/** Everything the stage resolver needs. Keep this a plain data bag so the resolver stays pure. */
export interface ReservationSnapshot {
  /** Card the hold was placed on — needed to attach the bank statement to the right card. */
  cardId: number | null;
  /** `cashier_settings.user_verified` from init. */
  isUserVerified: boolean;
  /** `kyc_verification_status` from `GET /v1/user-cards/{amount}` or the `/verifications` hub. */
  cardVerificationStatus: ReservationCardStatus | null;
  /**
   * `has_reserved_deposit` from `GET /v1/user-cards/{amount}` — this card already carries a hold
   * from an earlier session.
   *
   * Drives two things the contract requires: the card is preselected in the form, and re-entering
   * the widget lands the player back on that card's hold page instead of the methods list.
   */
  hasReservedDeposit: boolean;
  /**
   * ⚠️ NOT WIRED YET. Both would come from the notification channel (REQ.02 / REQ.11) once the
   * reserved/reversed `transaction_status` values are confirmed. Until then `isReversed` stays
   * `false`, which makes the `expired` screen unreachable in practice.
   */
  isCaptured: boolean;
  isReversed: boolean;
}

export interface ReservationState extends ReservationSnapshot {
  /** True once the snapshot has been resolved from the backend at least once. */
  isResolved: boolean;
}
